import { isNoiseHeadline } from "./noise";

// ─── Headlines written as advice ─────────────────────────────────────────────
//
// One list, one place (DECISIONS.md, 2026-09-26 · "מידע, לא ייעוץ"). In July
// 2026 the Israel Securities Authority stopped a bank from showing
// recommendation-style content inside a customer's portfolio page and "hot
// stocks" lists. Its test: personal fit + wording of an action. A headline
// that tells the reader what to do with a stock ("Should investors buy X?",
// "3 undervalued stocks") is exactly that once it sits next to the reader's
// own holding.
//
// So a headline matching this list is not shown on the home screen, in the
// portfolio, under "כתבות על החברה היום" or in the company page's news, and
// it is never the lead story of the general feed. To add or remove a phrase,
// edit the lists below · nothing else needs to change.

// English. Word boundaries, case-insensitive. "sell" does not match "sells"
// (a company selling units is a fact) or "sell-off"/"selloff" (a market
// move); "buy" does not match "buyback".
export const ADVISORY_EN = [
  "should (?:you|investors|i|we)",
  "how should",
  // "How Might Investors React To …" · the English of "כיצד משקיעים", which is
  // on the Hebrew list; it got through under a holding in translation.
  "how (?:might|will|could|can|do|are) investors",
  // Not "Best Buy" (a company), "buy-side" (a kind of firm) or a buyback.
  "(?<!best\\s)buy(?![-\\s]?(?:backs?|side))",
  "sell(?![-\\s]?offs?)",
  "undervalued",
  "overvalued",
  "top picks?",
  "cheap(?:est|er)?",
  "bargains?",
  "worth buying",
  "time to (?:buy|sell|get out)",
  "stocks? to (?:watch|own|avoid)",
  "must[- ]own",
  "no[- ]brainer",
  // Sprint 13, 1.5 · a price someone expects is a forecast, not a fact.
  // "Target" alone is not on the list: Target Corporation is a company, and
  // "inflation target" or "Fed lowers target rate" is a central bank's goal.
  // Caught instead: a price target, a target that belongs to someone ("our",
  // "analyst's", "Street"), and a target raised or cut ON a stock or TO a
  // dollar price. "their targets" is left out · companies meet targets.
  "price targets?",
  "target prices?",
  "(?:our|my|analysts?'?|street'?s?|wall street'?s?|consensus|12[- ]month) targets?",
  "(?:sets?|raises?|raised|lifts?|lifted|boosts?|boosted|hikes?|hiked|cuts?|lowers?|lowered|trims?|trimmed) (?:its |the |a )?targets? (?:on|to \\$)",
  // "upside" as room for a price to rise. Not a reported surprise ("upside
  // surprise", "surprised to the upside"), a central bank's "upside risks",
  // or "upside down".
  "(?<!to the )upside(?![- ](?:surprises?|down|risks?))",
  "downside to",
];

// Hebrew. Matched as whole words, with an optional prefix letter
// (ו/ה/ש/כ/ל/ב/מ), so "שכדאי" and "והזדמנות" are caught.
export const ADVISORY_HE = [
  "כיצד משקיעים",
  "איך משקיעים",
  "צריכים",
  "כדאי",
  "לקנות",
  "למכור",
  "זולה",
  "זול",
  "הזדמנות",
  "הזדמנויות",
  "מומלץ",
  "מומלצת",
  "מומלצות",
  "שווה לקנות",
];

const EN_RE = new RegExp(`\\b(?:${ADVISORY_EN.join("|")})\\b`, "i");
const HE_RE = new RegExp(`(^|[^\\u0590-\\u05FF])[והשכלבמ]?(?:${ADVISORY_HE.join("|")})(?=$|[^\\u0590-\\u05FF])`);

/** True when a headline (either language) is worded as advice. */
export function isAdvisoryHeadline(...texts) {
  return texts.some(t => typeof t === "string" && t && (EN_RE.test(t) || HE_RE.test(t)));
}

/** An article whose headline, original or translated, is worded as advice ·
 *  or, in the original, is a list or a promotion (constants/noise.js). Either
 *  way it is never tied to a holding. */
export function isAdvisoryArticle(article, translatedTitle) {
  return isAdvisoryHeadline(article?.title, translatedTitle) || isNoiseHeadline(article?.title);
}
